import {TheRaveSound} from "../sound/TheRaveSound";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {TimelineMax, SteppedEase, TweenMax} from "gsap";
import {ImgLoader} from "@nolimitcity/slot-launcher/bin/loader/ImgLoader";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";

export enum WooferStates {
    STOPPED = 'stopped',
    IDLE = 'idle',
    SMALL_BASS = 'smallBass',
    BIG_BASS = 'bigBass'
}

export enum FlasherStates {
    IDLE = 'idle',
    QUICK_FLASH = 'quickFlash',
    SLOW_FLASH = 'slowFlash',
    STROBE = 'strobe'
}

export enum DimmerStates {
    IDLE = 'idle',
    QUICK_DIM = 'quickDim',
    SLOW_DIM = 'slowDim'
}

export class AudioVisualiser extends PIXI.Container {

    private audioCtx: AudioContext;

    private barCoords: PIXI.Point = new PIXI.Point(720, 120);
    private bufferLength:number = 0;

    private activeArr: Uint8Array;
    private activeAnalyserNode: AnalyserNode;
    private activeMasterGain: GainNode;
    private created:boolean = false;
    // Woofer members
    private avLayer:StageLayer;
    private wooferOuterSprite:PIXI.Sprite;
    private wooferInnerSprite:PIXI.Sprite;
    private wooferIdleTl:TimelineMax;
    private wooferSmallBassTl:TimelineMax;
    private wooferBigBassTl:TimelineMax;
    private wooferCurrentState:string = "stopped";
    private wooferCurrentTl:TimelineMax = new TimelineMax();
    private filter:BiquadFilterNode;
    private filterConnected:boolean =  false;

    private barsLit: number [] = [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0];

    constructor( avLayer:StageLayer) {
        super();

        this.avLayer = avLayer;
        this.createWinAmpEQ();
        this.updateLitBars();

        const ref = this;
        NolimitApplication.apiPlugin.events.on('settingChange', function(message:any) {
            //console.log('SETTING CHANGE :: message: ');
            //console.log(message);

            // handle settings change from game
            if(message && message.name) {
                if(message.name === 'soundMaster') {
                    //console.log("Update sound settings: " + message.value);
                    if(!ref.audioCtx) {
                        //console.log("CREATING AV-Visualiser")
                        ref.tryCreateVisualiser();
                    }
                }
            }
        });

    }

    private tmpDebugHandler(): void {
        const ref = this;

        //console.log("AUDIO FILTER TESTING, press numpad:");
        //console.log("0: lowpass");
        //console.log("1: highpass");
        console.log("2: bandpass");
        console.log("3: lowshelf");
        console.log("4: highshelf");
        console.log("5: peaking");
        console.log("6: notch");
        console.log("9: reset :: allpass");

        document.addEventListener("keydown", function (e) {
            var keyCode = e.keyCode;
            //console.log("KeyCode: " + keyCode);

            switch (keyCode) {
                case 48:
                    ref.resetFilters();
                    ref.applyFilter('lowpass');
                    break; // 0
                case 49:
                    ref.resetFilters();
                    ref.applyFilter('highpass');
                    break; // 1
                case 50:
                    ref.resetFilters();
                    ref.applyFilter('bandpass');
                    break; // 2
                case 51:
                    ref.resetFilters();
                    ref.applyFilter('lowshelf');
                    break; // 3
                case 52:
                    ref.resetFilters();
                    ref.applyFilter('highshelf');
                    break; // 4
                case 53:
                    ref.resetFilters();
                    ref.applyFilter('peaking');
                    break; // 5
                case 54:
                    ref.resetFilters();
                    ref.applyFilter('notch');
                    break; // 6
                case 57:
                    ref.resetFilters();
                    break; // 9
            }
        }, false);
    }

    private fetchAudioCtx():void {
        this.audioCtx = TheRaveSound.soundPlayer.getContext();
    }

    private switchActiveWooferTl(state:string) {
        /*
            wooferIdleTl
            wooferSmallBaseTl
            wooferBigBassTl
        */

        if(state === WooferStates.STOPPED) {
            if(this.wooferCurrentTl) {
                this.wooferCurrentTl.pause(0);
                this.wooferCurrentTl = new TimelineMax();
            }
        }
        else if(state === WooferStates.IDLE) {
                this.wooferIdleTl = new TimelineMax({
                    repeat: -1,
                    paused: true,
                    ease: SteppedEase.config(3),
                    yoyo: true
                });
                this.wooferIdleTl.add(TweenMax.fromTo(this.wooferInnerSprite.scale, 0.1, {
                    x: 0.25,
                    y: 0.25
                }, {
                    x: 0.2535,
                    y: 0.2535
                }));
                this.wooferIdleTl.play(0);
                this.wooferCurrentTl = this.wooferIdleTl;
        }
        else if(state === WooferStates.BIG_BASS) {
            if(this.wooferCurrentTl)
                this.wooferCurrentTl.pause(0);
            if(!this.wooferBigBassTl) {
                // create woofer idle tl
                this.wooferBigBassTl = new TimelineMax({
                    paused: true
                });
                this.wooferBigBassTl.add(TweenMax.to(this.wooferInnerSprite.scale, 0.05,  {
                    x: 0.275,
                    y: 0.275
                }));
                this.wooferBigBassTl.add(TweenMax.to(this.wooferInnerSprite.scale, 0.02,  {
                    x: 0.25,
                    y: 0.25
                }));
                this.wooferBigBassTl.add(() => {
                    this.switchActiveWooferTl(WooferStates.IDLE);
                });

                this.wooferCurrentTl.pause();
                this.wooferBigBassTl.play(0);
                this.wooferCurrentTl = this.wooferBigBassTl;
            }
            else {
                if(this.wooferCurrentTl)
                    this.wooferCurrentTl.pause();
                this.wooferCurrentTl = this.wooferBigBassTl;
                this.wooferCurrentTl.play(0);
            }
        }
        else if(state === WooferStates.SMALL_BASS) {
            if(this.wooferCurrentTl)
                this.wooferCurrentTl.pause(0);
            if(!this.wooferSmallBassTl) {
                // create woofer idle tl
                this.wooferSmallBassTl = new TimelineMax({
                    paused: true
                });
                this.wooferSmallBassTl.add(TweenMax.to(this.wooferInnerSprite.scale, 0.05,  {
                    x: 0.2575,
                    y: 0.2575
                }));
                this.wooferSmallBassTl.add(TweenMax.to(this.wooferInnerSprite.scale, 0.02,  {
                    x: 0.25,
                    y: 0.25
                }));
                this.wooferSmallBassTl.add(() => {
                    this.switchActiveWooferTl(WooferStates.IDLE);
                });

                this.wooferCurrentTl.pause();
                this.wooferSmallBassTl.play(0);
                this.wooferCurrentTl = this.wooferSmallBassTl;
            }
            else {
                if(this.wooferCurrentTl)
                    this.wooferCurrentTl.pause();
                this.wooferCurrentTl = this.wooferSmallBassTl;
                this.wooferCurrentTl.play(0);
            }
        }
    }

    public filterToggle(on:boolean = false):void {

    }

    /**
     * @param filterType
     */

    public applyFilter(filterType:BiquadFilterType):void {
        if(this.audioCtx) {
            const filter = this.filter;
            let options: any;

            filter.type = filterType;

            if (!this.filterConnected) {
                if (filterType === 'lowpass') {
                    options = {
                        targetFrequency: 100,
                        startTime: this.audioCtx.currentTime,
                        timeConstant: 0
                    }
                    filter.frequency.setTargetAtTime(options.targetFrequency, this.audioCtx.currentTime + 0.5, options.timeConstant);
                    //filter.connect(this.audioCtx.destination);
                    this.activeMasterGain.connect(filter);
                    //console.log("Applyng filter: " + filterType);
                    this.filterConnected = true;
                }
                else if (filterType === 'highpass') {
                    options = {
                        targetFrequency: 2000,
                        startTime: this.audioCtx.currentTime,
                        timeConstant: 0
                    }
                    filter.frequency.setTargetAtTime(options.targetFrequency, this.audioCtx.currentTime + 0.5, options.timeConstant);
                    //filter.connect(this.audioCtx.destination);
                    this.activeMasterGain.connect(filter);
                    //console.log("Applyng filter: " + filterType);
                    this.filterConnected = true;
                } else {
                    //filter.connect(this.audioCtx.destination);
                    this.activeMasterGain.connect(filter);
                    //console.log("Applyng filter: " + filterType);
                    this.filterConnected = true;
                }


                if (options) {
                    //console.log("Filter options applied: ", options);
                } else {
                    //console.log("No filter options applied");
                }
            }
        }
    }

    public resetFilters():void {

        if(this.filterConnected) {
            this.activeMasterGain.disconnect(this.filter);
            this.filterConnected = false;
        }
        //this.applyFilter('allpass');
    }

    public tryCreateVisualiser():void {
        this.fetchAudioCtx();
        if(this.audioCtx !== null && !this.created) {
            this.created = true;


            const analyser:AnalyserNode = this.activeAnalyserNode = this.audioCtx.createAnalyser();
            this.filter = this.audioCtx.createBiquadFilter();
            //this.activeMasterGain.connect(this.filter);

            this.filter.connect(this.audioCtx.destination);

            //console.log("GAIN VALUE BEFORE ANALYSER:" + TheRaveSound.soundPlayer.getMasterGain().gain.value);
            const gainNode:GainNode = this.activeMasterGain = TheRaveSound.soundPlayer.getMasterGain();
            //console.log("GAIN NODE CHANNEL COUNT: " + gainNode.channelCount);


            gainNode.connect(analyser);
            analyser.fftSize = 256;

            analyser.smoothingTimeConstant = 0.85;

            const bufferLength = analyser.frequencyBinCount;
            this.bufferLength = bufferLength;
            //console.log('Buffer length: ' + bufferLength);
            const dataArr:Uint8Array = this.activeArr = new Uint8Array(bufferLength);


            // Get the Data array
            analyser.minDecibels = -80;
            analyser.maxDecibels = -10;
            //analyser.getByteFrequencyData(dataArr);

            //console.log('DATA ARRAY: ', dataArr);
            this.updateDataArrInterval(dataArr, analyser);

            //this.tmpDebugHandler();
        }
    }

    setEqConfigurationState(state:string = ""):void {

    }

    private updateDataArrInterval(dataArr:Uint8Array, analyserNode:AnalyserNode, intervalMs:number = 1000/33): void {
        const ref = this;
        setInterval(function(){
            //ref.activeAnalyserNode.minDecibels = -80;
            //ref.activeAnalyserNode.maxDecibels = -10;
            //console.log('DATA ARRAY ANALYSER members, fftSize: ' + ref.activeAnalyserNode.fftSize);
            //console.log('DATA ARRAY ANALYSER members, frequencyBinCount: ' + ref.activeAnalyserNode.frequencyBinCount);
            //console.log('DATA ARRAY ANALYSER members, minDb: ' + ref.activeAnalyserNode.minDecibels);
            //console.log('DATA ARRAY ANALYSER members, maxDb: ' + ref.activeAnalyserNode.maxDecibels);
            //console.log('DATA ARRAY ANALYSER members, channelCount: ' + ref.activeAnalyserNode.channelCount);
            //console.log('DATA ARRAY ANALYSER members, channelCount: ' + ref.activeAnalyserNode.);

            ref.activeAnalyserNode.getByteFrequencyData(ref.activeArr);
            //console.log('DATA ARRAY LENGTH: ' + dataArr.length)
            let cntr:number = 0;
            for(let x = 1; x < dataArr.length; x+=2) {

                ref.barsLit[cntr] = Math.round(dataArr[x] / 23);
                cntr++
            }

            //console.log('MAX FREQ', ref.maxFrqValue);
            ref.updateLitBars();
        }, intervalMs);
    }


    private doStuffOnDifferentFrequencies(frequency:number = -1, dataArr:Uint8Array = new Uint8Array()) {
        if(frequency > -1) {
            switch(frequency) {
                case 10:
                    if(dataArr.length >= frequency && dataArr[frequency] > 100) {
                        console.log(dataArr[frequency]);

                        if(dataArr[frequency] >= 105 && dataArr[frequency] <= 115) {
                            this.switchActiveWooferTl(WooferStates.SMALL_BASS);
                        }
                        if(dataArr[frequency] >= 115) {
                            this.switchActiveWooferTl(WooferStates.BIG_BASS);
                        }
                    }
                    break;
                default: break;
            }
        }
    }

    private createWinAmpEQ():void {
        const barWrapper:PIXI.Container = new PIXI.Container();
        const bgSprite:PIXI.Sprite = new PIXI.Sprite(ImgLoader.getImgTexture("eqBg"));;

        // green startPosFrom bottom
        const greenYstart:number = 68;
        const greenColXStart:number = 6;

        barWrapper.width = 148;
        barWrapper.height = 118;
        barWrapper.position.set(-3, 60);
        barWrapper.addChild(bgSprite);

        const colStartIndexes: number [] = [0, 5, 9, 11];



        for(let i = 0; i < 15; i++) {
            let spritesForColumn: PIXI.Sprite [] = [];
            spritesForColumn = spritesForColumn.concat(
                this.createBarCol(barWrapper, i, colStartIndexes[0], colStartIndexes[1], greenColXStart, greenYstart, "eqGreen"),
                this.createBarCol(barWrapper, i, colStartIndexes[1], colStartIndexes[2], greenColXStart, greenYstart, "eqYellow"),
                this.createBarCol(barWrapper, i, colStartIndexes[2], colStartIndexes[3], greenColXStart, greenYstart, "eqRed")
            );
            this.barSprites.push(spritesForColumn)
        }

        this.avLayer.addChild(barWrapper);
    }

    private updateLitBars():void {
        if(this.barsLit) {
            this.barsLit.forEach((litNr, colNr) => {
                if (this.barSprites[colNr]) {
                    this.barSprites[colNr].forEach((sprite, barIndex) => {
                        if (barIndex <= litNr) {
                            sprite.visible = true;
                        } else {
                            sprite.visible = false;
                        }

                        if(litNr === 0) {
                            sprite.alpha = 0.5;
                        }
                        else {
                            sprite.alpha = 1;
                        }
                    });

                }
            })
        }
    }

    private barSprites: PIXI.Sprite [][] = [];

    createBarCol(wrapper:PIXI.Container, colIndex: number, startIndex:number, endIndex:number, startX:number, startY:number, textureName:string):PIXI.Sprite [] {

        const tmpBar:PIXI.Sprite = new PIXI.Sprite(ImgLoader.getImgTexture(textureName));
        const columnSprites:PIXI.Sprite [] = [];

        const colOffset:number = tmpBar.height + 1;
        const rowOffset:number = tmpBar.width + 0.9;

        for(let x = startIndex; x < endIndex; x++) {
            const sprite:PIXI.Sprite = new PIXI.Sprite(ImgLoader.getImgTexture(textureName));
            sprite.name = "col_" + colIndex + "_" + textureName + "_" + x;

            sprite.x = startX + (rowOffset * colIndex);
            sprite.y = startY - (colOffset * x);

            wrapper.addChild(sprite);
            columnSprites.push(sprite);
        }

        return columnSprites;
    }

    private createBarGraph(bufferLength:number):void {

        // Bar BG
        this.removeChild(this.children[0]);
        if(bufferLength > 0) {
            const areaWidth: number = 250;
            const areaHeight: number = 200;
            const areaSprite = new PIXI.Sprite(PIXI.Texture.WHITE);
            areaSprite.tint = 0x777777;
            areaSprite.alpha = 0.75;

            areaSprite.width = areaWidth;
            areaSprite.height = areaHeight;
            areaSprite.position.set(this.barCoords.x, this.barCoords.y);

            this.addChild(areaSprite);

            // Individual bars
            let barWidth = (areaWidth / bufferLength) * 0.75;
            let barHeight;
            let x = 0;


            for(let i = 0; i < this.bufferLength; i++) {
                //debugger;

                if(i < 52) {
                    const barSpr = new PIXI.Sprite(PIXI.Texture.WHITE);

                    //barSpr.anchor.y = 0.5;
                    barSpr.scale.y = -1;
                    barSpr.pivot.y = -1;
                    barSpr.height = (barHeight = this.activeArr[i] / 2) * 0.15;


                    //barHeight = 5;
                    barSpr.width = barWidth / 40;

                    if(this.activeArr[i] > 200) {
                        barSpr.tint = PIXI.utils.rgb2hex([0, 0, (barHeight * 100) / 255]);
                    }
                    else {
                        barSpr.tint = PIXI.utils.rgb2hex([(barHeight * 100) / 255, 0, 0]);
                    }
                    barSpr.tint = PIXI.utils.rgb2hex([(barHeight * 100) / 255, 0, 0]);

                    barSpr.x += i + barSpr.width/4;//(((barWidth) + 0.1) * 0.2) * i;
                    barSpr.y = 16.8; //areaHeight/2;

                    areaSprite.addChild(barSpr);
                }
            }
        }
    }

}